/**
 * @fileoverview Text input — catalyst Input adapted for light-mode intake forms.
 *
 * Supports all standard HTML input types. Renders the characteristic
 * inset-shadow + subtle-border treatment from catalyst, minus dark-mode styles.
 */
import clsx from 'clsx';
import React, { forwardRef } from 'react';

export interface FormInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Visual-only invalid flag (red border) */
  invalid?: boolean;
}

export const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  ({ className, invalid, ...props }, ref) => (
    <span
      data-slot="control"
      className={clsx(
        'relative block w-full',
        // Inset pseudo for shadow that blends with border
        'before:absolute before:inset-px before:rounded-[7px] before:bg-white before:shadow-sm',
        // Focus ring
        'after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-transparent after:ring-inset',
        'sm:focus-within:after:ring-2 sm:focus-within:after:ring-blue-500',
        // Disabled
        'has-[:disabled]:opacity-50 has-[:disabled]:before:bg-zinc-50 has-[:disabled]:before:shadow-none',
        // Invalid
        invalid && 'before:shadow-red-500/10',
      )}
    >
      <input
        ref={ref}
        className={clsx(
          className,
          // Layout
          'relative block w-full appearance-none rounded-lg',
          'px-3 py-2 sm:px-3 sm:py-1.5',
          // Typography
          'text-sm text-zinc-900 placeholder:text-zinc-400',
          // Border
          'border border-zinc-200',
          'hover:border-zinc-300',
          // Background
          'bg-transparent',
          // Focus
          'focus:outline-none',
          // Invalid
          invalid && 'border-red-400 hover:border-red-400',
          // Disabled
          'disabled:border-zinc-200 disabled:cursor-not-allowed',
        )}
        {...props}
      />
    </span>
  ),
);

FormInput.displayName = 'FormInput';
